import React, { useState, useEffect } from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';


const GraduationDays = () => {
  const [graduationDays, setGraduationDays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [formData, setFormData] = useState({
    ceremony_date: '',
    venue: '',
    chief_guest: ''
  });
  const [loading, setLoading] = useState(false);
  const [settingActive, setSettingActive] = useState(false);

  useEffect(() => {
    fetchGraduationDays();
  }, []);

  const fetchGraduationDays = async () => {
    setLoading(true);
    try {
      const response = await api.get('/graduation-days');
      setGraduationDays(response.data);
    } catch (error) {
      console.error('Error fetching graduation days:', error);
      toast.error('Failed to fetch graduation days');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingDay) {
        await api.put(`/graduation-days/${editingDay.id}`, formData);
        toast.success('Graduation day updated successfully');
      } else {
        await api.post('/graduation-days', formData);
        toast.success('Graduation day created successfully');
      }
      setShowForm(false);
      setEditingDay(null);
      setFormData({ ceremony_date: '', venue: '', chief_guest: '' });
      fetchGraduationDays();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving graduation day');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (day) => {
    setEditingDay(day);
    setFormData({
      ceremony_date: day.ceremony_date.split('T')[0],
      venue: day.venue || '',
      chief_guest: day.chief_guest || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this graduation day? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/graduation-days/${id}`);
      toast.success('Graduation day deleted successfully');
      fetchGraduationDays();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting graduation day');
    } finally {
      setLoading(false);
    }
  };

  const handleSetActive = async (id) => {
    if (!window.confirm('Set this as the active graduation day? This will deactivate all other graduation days.')) {
      return;
    }

    setSettingActive(true);
    try {
      await api.put(`/graduation-days/${id}/set-active`);
      toast.success('Active graduation day updated successfully');
      fetchGraduationDays();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error setting active graduation day');
    } finally {
      setSettingActive(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isUpcoming = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ceremonyDate = new Date(dateString);
    return ceremonyDate >= today;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Graduation Days</h1>
          <p className="text-sm text-gray-600 mt-1">Manage graduation ceremonies and set active date</p>
        </div>
        <Button onClick={() => {
          setEditingDay(null);
          setFormData({ ceremony_date: '', venue: '', chief_guest: '' });
          setShowForm(true);
        }}>
          + Schedule Graduation
        </Button>
      </div>

      {/* Active Graduation Day Banner */}
      {graduationDays.some(day => day.is_active) && (
        <Card className="mb-6 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-2xl">🎓</div>
              <div>
                <h3 className="font-semibold text-green-800">Active Graduation Day</h3>
                <p className="text-sm text-green-700">
                  {graduationDays.find(day => day.is_active)?.venue} - {formatDate(graduationDays.find(day => day.is_active)?.ceremony_date)}
                </p>
              </div>
            </div>
            <span className="px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm font-semibold">
              Active
            </span>
          </div>
        </Card>
      )}

      {/* Form Modal/Card */}
      {showForm && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingDay ? 'Edit Graduation Day' : 'Schedule New Graduation'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <Input
                label="Ceremony Date *"
                type="date"
                value={formData.ceremony_date}
                onChange={(e) => setFormData({...formData, ceremony_date: e.target.value})}
                required
              />
              <Input
                label="Venue *"
                value={formData.venue}
                onChange={(e) => setFormData({...formData, venue: e.target.value})}
                placeholder="e.g., Katinetti Institute Graduation Square"
                required
              />
              <Input
                label="Chief Guest"
                value={formData.chief_guest}
                onChange={(e) => setFormData({...formData, chief_guest: e.target.value})}
                placeholder="e.g., Cabinet Secretary - Ministry of Education"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingDay ? 'Update' : 'Save')}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowForm(false);
                setEditingDay(null);
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Tabular List */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Venue
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Chief Guest
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {graduationDays.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                  No graduation days scheduled. Click "Schedule Graduation" to add one.
                </td>
              </tr>
            ) : (
              graduationDays.map((day) => (
                <tr key={day.id} className={`hover:bg-gray-50 ${day.is_active ? 'bg-green-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatDate(day.ceremony_date)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {day.day}/{day.month}/{day.year}
                    </div>
                    {isUpcoming(day.ceremony_date) && !day.is_active && (
                      <span className="text-xs text-blue-600">Upcoming</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{day.venue || '—'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{day.chief_guest || '—'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      day.is_active
                        ? 'bg-green-100 text-green-800'
                        : isUpcoming(day.ceremony_date)
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {day.is_active ? 'Active' : isUpcoming(day.ceremony_date) ? 'Upcoming' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {day.created_by_name || 'System'}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    {!day.is_active && (
                      <button
                        onClick={() => handleSetActive(day.id)}
                        disabled={settingActive}
                        className="text-green-600 hover:text-green-900 mr-3"
                      >
                        Set Active
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(day)}
                      className="text-primary hover:text-primary-dark mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(day.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GraduationDays;
